import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import { Typography, IconButton, Tooltip, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, ListItem, ListItemText, Menu, MenuItem, Snackbar, Alert } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ShareIcon from '@mui/icons-material/Share';
import { fetchWithAuth } from "../../utils/auth";
import API_BASE_URL from '../../config/api';
import { getThemeAwareColor, getCategoryStyles } from '../../utils/categoryColors';
import { useTheme } from '../../utils/ThemeContext';

// Use valueFormatter for simple text columns (better performance than renderCell)
// eslint-disable-next-line no-unused-vars
const truncateAbstract = (value) => value && value.length > 150 ? `${value.substring(0, 150)}...` : value;

// Helper function for prediction colors
const getPredictionColors = (prediction) => {
  if (prediction === 'yes') return { color: 'var(--success)', bg: 'rgba(70, 130, 48, 0.25)' };
  if (prediction === 'maybe') return { color: 'var(--warning)', bg: 'rgba(255, 193, 7, 0.25)' };
  if (prediction === 'no') return { color: 'var(--error)', bg: 'rgba(244, 67, 54, 0.25)' };
  return { color: 'var(--text-secondary)', bg: 'transparent' };
};

// Memoized Link Cell Component
const LinkCell = React.memo(({ value }) => {
  const href = value;
  if (!href) return <span style={{ opacity: 0.7 }}>—</span>;
  return (
    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      <a 
        href={href} 
        target="_blank" 
        rel="noopener noreferrer"
        style={{ color: 'var(--text-primary)', textDecoration: 'none', fontWeight: 'bold' }}
        onClick={(e) => e.stopPropagation()}
        title={href}
      >
        {href}
      </a>
    </span>
  );
});

// Memoized Actions Cell Component - optimized with primitives
const ActionsCell = React.memo(({ id, read, onToggleRead, onShare }) => {
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <Tooltip title={read ? "Mark as Unread" : "Mark as Read"} disableInteractive>
        <IconButton size="small" onClick={(e) => onToggleRead(id, e)}>
          <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Share with Colleagues" disableInteractive>
        <IconButton size="small" onClick={(e) => onShare({ id }, e)}>
          <ShareIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
});

// Memoized Prediction Cell Component with dropdown - optimized with primitives
const PredictionCell = React.memo(({ id, prediction, confidence, modified, origPred, origConf, onEditCommit }) => {
  const { color, bg } = getPredictionColors(prediction);
  
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  const handleSelect = (newValue) => {
    if (newValue !== prediction) {
      onEditCommit({ 
        id, 
        field: 'ai_prediction', 
        value: newValue 
      }, null);
    }
    handleClose();
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }} data-tutorial="prediction-cell">
      <Box
        onClick={handleClick}
        title={modified && origPred && origConf 
          ? `Original AI: ${origPred} (${(origConf * 100).toFixed(0)}%). Click to change.` 
          : 'Click to change classification'}
        sx={{
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          px: 1, 
          py: 0.75, 
          borderRadius: 1, 
          backgroundColor: bg, 
          border: `1px solid ${color}`,
          minHeight: 48, 
          width: '100%', 
          cursor: 'pointer', 
          position: 'relative',
          '&:hover': { 
            borderWidth: 2, 
            borderColor: '#6c3082' 
          }
        }}
      >
        {modified && (
          <Box sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            width: 8,
            height: 8,
            borderRadius: '50%',
            backgroundColor: 'var(--neon-cyan)',
            border: '1px solid var(--surface)',
          }} />
        )}
        <Typography variant="caption" sx={{ fontWeight: 700, color, textTransform: 'uppercase', fontSize: 11 }}>
          {prediction}
        </Typography>
        {!modified && confidence != null && (
          <Typography variant="caption" sx={{ color, fontSize: 9, mt: 0.25 }}>
            {(confidence * 100).toFixed(0)}%
          </Typography>
        )}
        {modified && origPred && origConf != null && (
          <Typography variant="caption" sx={{ color: 'var(--text-muted)', fontSize: 8, mt: 0.25 }}>
            AI: {origPred} {(origConf * 100).toFixed(0)}%
          </Typography>
        )}
      </Box>
      
      {/* Custom Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            minWidth: 120,
          }
        }}
      >
        <MenuItem 
          onClick={() => handleSelect('yes')}
          sx={{ 
            fontSize: 12, 
            fontWeight: 'bold', 
            color: 'var(--success)',
            backgroundColor: 'var(--surface-2) !important',
            borderRadius: '6px !important',
            margin: '2px !important',
            '&:hover': {
              backgroundColor: 'rgba(108, 48, 130, 0.1) !important',
              borderRadius: '6px !important'
            }
          }}
        >
          ✓ YES
        </MenuItem>
        <MenuItem 
          onClick={() => handleSelect('maybe')}
          sx={{ 
            fontSize: 12, 
            fontWeight: 'bold', 
            color: 'var(--warning)',
            backgroundColor: 'var(--surface-2) !important',
            borderRadius: '6px !important',
            margin: '2px !important',
            '&:hover': {
              backgroundColor: 'rgba(108, 48, 130, 0.1) !important',
              borderRadius: '6px !important'
            }
          }}
        >
          ? MAYBE
        </MenuItem>
        <MenuItem 
          onClick={() => handleSelect('no')}
          sx={{ 
            fontSize: 12, 
            fontWeight: 'bold', 
            color: 'var(--error)',
            backgroundColor: 'var(--surface-2) !important',
            borderRadius: '6px !important',
            margin: '2px !important',
            '&:hover': {
              backgroundColor: 'rgba(108, 48, 130, 0.1) !important',
              borderRadius: '6px !important'
            }
          }}
        >
          ✗ NO
        </MenuItem>
      </Menu>
    </Box>
  );
});

// Memoized Category Cell Component with dropdown - similar to PredictionCell
// Custom comparison function to properly detect categories array changes
const CategoryCell = React.memo(({ row, categories, onCategoryChange, isDarkMode }) => {
  const categoryId = row.category_id;
  const category = categories.find(cat => cat.id === categoryId);
  
  const [isOpen, setIsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  const handleSelect = (categoryId) => {
    if (onCategoryChange) {
      onCategoryChange(row, categoryId);
    }
    handleClose();
  };

  // Get theme-aware color for the current category
  const themeColor = category ? getThemeAwareColor(category.color, isDarkMode, 0) : 'var(--text-muted)';
  const bg = category ? getCategoryStyles(category.color, isDarkMode, false).backgroundColor : 'transparent';

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }} data-tutorial="category-button">
      <Box
        onClick={handleClick}
        title={category ? `Category: ${category.name}. Click to change.` : 'Click to add category'}
        sx={{
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          px: 1, 
          py: 0.75, 
          borderRadius: 1, 
          backgroundColor: bg, 
          border: category ? `1px solid ${themeColor}` : '1px dashed var(--text-muted)',
          minHeight: 48, 
          width: '100%', 
          cursor: 'pointer', 
          position: 'relative',
          '&:hover': { 
            borderWidth: 2, 
            borderColor: category ? themeColor : '#6c3082'
          }
        }}
      >
        {category ? (
          <>
            {category.icon && (
              <Typography variant="caption" sx={{ fontSize: 14, lineHeight: 1 }}>
                {category.icon}
              </Typography>
            )}
            <Typography variant="caption" sx={{ fontWeight: 700, color: themeColor, fontSize: 11, textTransform: 'uppercase', mt: category.icon ? 0.25 : 0, lineHeight: 1 }}>
              {category.name}
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="caption" sx={{ fontSize: 14, lineHeight: 1 }}>
              🏷️
            </Typography>
            <Typography variant="caption" sx={{ fontWeight: 700, color: 'var(--text-muted)', fontSize: 11, textTransform: 'uppercase', mt: 0.25, lineHeight: 1 }}>
              Add
            </Typography>
          </>
        )}
      </Box>
      
      {/* Custom Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--surface-2)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            minWidth: 140,
            maxHeight: 300,
          }
        }}
      >
        {/* Remove category option */}
        {category && (
          <MenuItem 
            onClick={() => handleSelect(null)}
            sx={{ 
              fontSize: 11, 
              fontWeight: 'bold', 
              color: 'var(--text-muted)',
              backgroundColor: 'var(--surface-2) !important',
              borderRadius: '6px !important',
              margin: '2px !important',
              borderBottom: '1px solid var(--border)',
              mb: '4px !important',
              '&:hover': {
                backgroundColor: 'rgba(244, 67, 54, 0.1) !important',
                color: 'var(--error)',
                borderRadius: '6px !important'
              }
            }}
          >
            ✗ Remove Category
          </MenuItem>
        )}
        
        {/* Category options */}
        {categories.length === 0 ? (
          <MenuItem 
            disabled
            sx={{ 
              fontSize: 11, 
              fontStyle: 'italic',
              color: 'var(--text-muted)',
              backgroundColor: 'var(--surface-2) !important',
            }}
          >
            No categories yet
          </MenuItem>
        ) : (
          categories.map((cat) => {
            const catThemeColor = getThemeAwareColor(cat.color, isDarkMode, 0);
            return (
              <MenuItem 
                key={cat.id}
                onClick={() => handleSelect(cat.id)}
                sx={{ 
                  fontSize: 11, 
                  fontWeight: category?.id === cat.id ? 'bold' : 600,
                  color: catThemeColor,
                  backgroundColor: category?.id === cat.id ? `${catThemeColor}15` : 'var(--surface-2) !important',
                  borderRadius: '6px !important',
                  margin: '2px !important',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  '&:hover': {
                    backgroundColor: `${catThemeColor}20 !important`,
                    borderRadius: '6px !important'
                  }
                }}
              >
                {cat.icon && <span style={{ fontSize: 12 }}>{cat.icon}</span>}
                <span>{cat.name}</span>
                {category?.id === cat.id && <span style={{ marginLeft: 'auto', fontSize: 10 }}>✓</span>}
              </MenuItem>
            );
          })
        )}
      </Menu>
    </Box>
  );
}, (prevProps, nextProps) => {
  // Custom comparison: re-render if categories array length changed or row.category_id changed
  return (
    prevProps.row.category_id === nextProps.row.category_id &&
    prevProps.categories.length === nextProps.categories.length &&
    prevProps.isDarkMode === nextProps.isDarkMode
  );
});



// Removed global styles that were causing conflicts with other menus

// Remove global style injection - it was causing conflicts with other menus
// The DataGrid styling will be handled through the sx prop instead

const DynamicTable = React.memo(({ data: propData, onRowClick, onReadStatusChange, onArticleUpdate, currentPage = 0, onPageChange, domain = '', onAIFilteringSummary, hiddenNoArticles = false, categories = [], onCategoryChange }) => {
  const { isDarkMode } = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  // Single controlled pagination model
  const [paginationModel, setPaginationModel] = useState({ page: currentPage, pageSize: 5 });
  
  // AI Filtering state
  const [aiFiltering, setAiFiltering] = useState(false);
  const [aiFilteredData, setAiFilteredData] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [originalAiFilteredData, setOriginalAiFilteredData] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [hasTriggeredFiltering, setHasTriggeredFiltering] = useState({ domain: '', dataLength: 0 });
  
  // DataGrid API ref for native editing
  const apiRef = useGridApiRef();
  
  // Create stable refs for handlers so columns don't rebuild on every state change
  const handleMarkAsReadRef = React.useRef(null);
  const handleShareArticleRef = React.useRef(null);
  
  // Ref for onAIFilteringSummary to prevent infinite loops
  const onAIFilteringSummaryRef = React.useRef(onAIFilteringSummary);
  onAIFilteringSummaryRef.current = onAIFilteringSummary;

  // Snackbar notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showNotification = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };
  
  // Memoize the DataGrid styles to prevent recreation on every render
  const dataGridStyles = useMemo(() => ({
    backgroundColor: 'var(--surface)',
    color: 'var(--text-primary)',
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    '& .MuiDataGrid-cell': {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      overflowWrap: 'break-word',
      lineHeight: '1.5',
      padding: '8px 10px', // Slightly reduced for performance
      backgroundColor: 'var(--surface)',
      color: 'var(--text-primary)',
      borderBottom: '1px solid var(--border)',
      // No box-shadow or transitions for performance
    },
    '& .MuiDataGrid-row': {
      backgroundColor: 'var(--surface)',
      // Removed marginBottom - breaks virtualization and causes layout thrash
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.04)', // Light hover only
      },
    },
    '& .MuiDataGrid-columnHeaders': {
      backgroundColor: 'var(--surface-2)',
      color: 'var(--text-primary)',
      borderBottom: '2px solid var(--text-primary)',
    },
    '& .MuiDataGrid-columnHeader': {
      backgroundColor: 'var(--surface-2)',
      color: 'var(--text-primary)',
    },
    '& .MuiDataGrid-columnHeader .MuiIconButton-root': {
      color: 'var(--text-primary)',
      '&:hover': {
        backgroundColor: 'rgba(108, 48, 130, 0.1)',
        color: 'var(--neon-cyan)',
      },
    },
    '& .MuiDataGrid-menuIcon': {
      color: '#6c3082',
      opacity: 1,
      '&:hover': {
        backgroundColor: 'rgba(108, 48, 130, 0.2)',
        color: '#6c3082',
        transform: 'scale(1.1)',
        boxShadow: '0 2px 8px rgba(108, 48, 130, 0.3)',
      },
      transition: 'all 0.2s ease-in-out',
    },
    '& .MuiDataGrid-menuIconButton': {
      color: '#6c3082',
      opacity: 1,
      backgroundColor: 'rgba(108, 48, 130, 0.1)',
      border: '1px solid rgba(108, 48, 130, 0.2)',
      borderRadius: 'var(--radius-sm)',
      '&:hover': {
        backgroundColor: 'rgba(108, 48, 130, 0.3)',
        border: '1px solid rgba(108, 48, 130, 0.4)',
        color: '#6c3082',
        transform: 'scale(1.05)',
        boxShadow: '0 2px 8px rgba(108, 48, 130, 0.3)',
      },
      transition: 'all 0.2s ease-in-out',
    },
    '& .MuiDataGrid-columnHeader--sortable .MuiIconButton-root': {
      color: 'var(--text-primary)',
      '&:hover': {
        backgroundColor: 'rgba(108, 48, 130, 0.1)',
        color: 'var(--neon-cyan)',
      },
    },
    '& .MuiDataGrid-footerContainer': {
      backgroundColor: 'var(--surface-2)',
      color: 'var(--text-primary)',
      borderTop: '1px solid var(--border)',
    },
    '& .MuiDataGrid-virtualScroller': {
      backgroundColor: 'var(--surface)',
    },
    '& .MuiDataGrid-virtualScrollerContent': {
      backgroundColor: 'var(--surface)',
    },
    '& .MuiDataGrid-virtualScrollerRenderZone': {
      backgroundColor: 'var(--surface)',
    },
    // Style the native singleSelect editor
    '& .MuiDataGrid-cell--editing .MuiSelect-select': {
      backgroundColor: 'var(--surface-2) !important',
      border: '1px solid var(--border) !important',
      borderRadius: '4px !important',
    },
    '& .MuiDataGrid-cell--editing .MuiSelect-icon': {
      color: 'var(--text-primary) !important',
    },
    '& .MuiDataGrid-row.unread-row': {
      fontWeight: 'bold',
      backgroundColor: 'rgba(255, 193, 7, 0.3)', // More visible yellow/orange for unread
      borderLeft: '4px solid #ffc107', // Yellow border for unread
      '& .MuiDataGrid-cell': {
        fontWeight: 'bold',
        color: 'var(--text-primary)',
      },
    },
    '& .MuiDataGrid-row.unread-row:hover': {
      backgroundColor: 'rgba(255, 193, 7, 0.4)',
    },
    '& .MuiDataGrid-row.read-row': {
      backgroundColor: 'rgba(70, 130, 48, 0.3)', // More visible green for read
      borderLeft: '4px solid #468230', // Green border for read
      '& .MuiDataGrid-cell': {
        fontWeight: 'normal',
        color: 'var(--text-primary)',
      },
    },
    '& .MuiDataGrid-row.read-row:hover': {
      backgroundColor: 'rgba(70, 130, 48, 0.4)',
    },
    '& .MuiTablePagination-root': {
      color: 'var(--text-primary)',
    },
    '& .MuiTablePagination-select': {
      color: 'var(--text-primary)',
    },
    '& .MuiTablePagination-selectIcon': {
      color: 'var(--text-primary)',
    },
    '& .MuiIconButton-root': {
      color: 'var(--text-primary)',
    },
    '& .MuiIconButton-root:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  }), []);
  
  // Sharing states
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [shareMessage, setShareMessage] = useState('');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [lastSharedUser, setLastSharedUser] = useState(() => {
    // Load last shared user from localStorage - only if authenticated
    try {
      const token = localStorage.getItem('authToken');
      if (!token) {
        return null;
      }
      
      const saved = localStorage.getItem('lastSharedUser');
      return saved ? JSON.parse(saved) : null;
    } catch (error) {
      console.error('Error loading last shared user:', error);
      return null;
    }
  });
  

  useEffect(() => {
    if (propData) {
      // Use data passed as props
      
      // Use stable unique ID from data instead of index
      const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
      
      const formattedData = propData.map((item) => ({
        id: toRowId(item),
        ...item,
        read: !!item.read,
        read_date: item.read_date ?? null,
      }));
      
      // Update data - no need for forced re-renders with stable IDs
      setData(formattedData);
      
      // ALWAYS use propData - parent (HomePage) is the source of truth
      // Parent updates immediately via onArticleUpdate callback
      if (formattedData.length > 0 && formattedData.some(item => item.ai_prediction)) {
        console.log('📦 Setting aiFilteredData from propData (parent is source of truth)');
        setAiFilteredData(formattedData);
      }
      
      setLoading(false);
    } else {
      // Fetch data from API if no props provided
      const fetchData = async () => {
        try {
          const response = await fetchWithAuth('/api/get-data');
          const result = await response.json();

          // Use stable unique ID from data instead of index
          const toRowId = (item) => item.doi || item.link || `${item.title}-${item.year}`;
          
    const formattedData = result.map((item) => ({
      id: toRowId(item),
            ...item,
      read: !!item.read,
      read_date: item.read_date ?? null,
      // Precompute truncated abstract to avoid valueFormatter work
      truncated_abstract: item.abstract && item.abstract.length > 150
        ? item.abstract.slice(0, 150) + '...'
        : item.abstract,
      // Precompute truncated title to avoid valueFormatter work
      truncated_title: item.title && item.title.length > 100
        ? item.title.slice(0, 100) + '...'
        : item.title,
          }));
          setData(formattedData);
          setLoading(false);
        } catch (error) {
          console.error('Error fetching data:', error);
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [propData]);  // Run when propData changes

  const handleMarkAsRead = useCallback(async (id, event) => {
    // Prevent any event bubbling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    const article = data.find(row => row.id === id);
    
    if (article) {
        const newReadStatus = !article.read;
      
      // Only notify parent component - let parent handle all updates
        if (onReadStatusChange) {
        await onReadStatusChange(id, newReadStatus);
      }
    }
  }, [data, onReadStatusChange]);

  const loadUsers = async () => {
    try {
      const response = await fetchWithAuth('/api/collaboration/users');
      const payload = await response.json();
      
      if (payload.error) {
        console.error('Error loading users:', payload.error);
      } else {
        setUsers(payload.users || []);
      }
    } catch (err) {
      console.error('Error loading users:', err);
    }
  };

  const handleShareArticle = useCallback((article, event) => {
    // Prevent any event bubbling
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log('📤 Sharing article:', article);
    
    // Set the article to share and open the share dialog
    setSelectedArticle(article);
    setShareDialogOpen(true);
    loadUsers(); // Load users for selection
  }, []);

  const handleShareToUser = async () => {
    if (!selectedUser || !selectedArticle) return;
    
    try {
      setSending(true);
      
      const response = await fetchWithAuth('/api/collaboration/share-items', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipient_id: selectedUser.id,
          items: [{
            type: 'article',
            id: selectedArticle.id,
            title: selectedArticle.title || 'No title',
            authors: selectedArticle.authors || 'Unknown Authors',
            journal: selectedArticle.journal || 'Unknown Journal',
            year: selectedArticle.year || 'Unknown Year',
            abstract: selectedArticle.abstract || 'No abstract available',
            description: selectedArticle.description || '',
            url: selectedArticle.link || selectedArticle.url || ''
          }],
          share_type: 'article',
          message: shareMessage.trim() || `Shared article with you`
        })
      });
      
      const result = await response.json();
      
      if (result.error) {
        showNotification('Error sharing article: ' + result.error, 'error');
      } else {
        showNotification('Article shared successfully!', 'success');
        // Track the last shared user and save to localStorage
        setLastSharedUser(selectedUser);
        try {
          localStorage.setItem('lastSharedUser', JSON.stringify(selectedUser));
        } catch (error) {
          console.error('Error saving last shared user:', error);
        }
        setShareDialogOpen(false);
        setSelectedUser(null);
        setShareMessage('');
        setUserSearchQuery('');
        setSelectedArticle(null);
      }
    } catch (err) {
      console.error('Error sharing article:', err);
      showNotification('Error sharing article: ' + err.message, 'error');
    } finally {
      setSending(false);
    }
  };

  const handleRowClick = useCallback(async (params) => {
    // Mark article as read when clicked (if it's unread)
    const article = data.find(row => row.id === params.id);
    if (article && !article.read) {
      await handleMarkAsRead(params.id);
    }
    
    // Call the original onRowClick handler
    if (onRowClick) {
      // Find the original article data from propData to preserve full abstract
      const originalArticle = propData ? propData.find(article => 
        article.title === params.row.title && 
        article.authors === params.row.authors &&
        article.year === params.row.year
      ) : params.row;
      
      // Use original article data if found, otherwise fall back to row data
      onRowClick(originalArticle || params.row);
    }
  }, [data, handleMarkAsRead, onRowClick, propData]);
  
  // Update handler refs
  handleMarkAsReadRef.current = handleMarkAsRead;
  handleShareArticleRef.current = handleShareArticle;

  // IBU Filtering handler
  const handleAIFiltering = useCallback(async () => {
    if (aiLoading) {
      return;
    }
    
    // Guard clause: don't filter if no data
    if (!data || data.length === 0) {
      return;
    }
    
    setAiLoading(true);
    try {
      // Prepare articles for filtering
      const articles = data.map(article => ({
        id: article.id,
        title: article.title,
        authors: article.authors,
        journal: article.journal,
        year: article.year,
        abstract: article.abstract,
        link: article.link
      }));

      // Determine the API endpoint based on domain
      const apiEndpoint = domain === 'IBU' ? '/api/ibu/filter-articles' : `/api/${domain.toLowerCase()}/filter-articles`;
      
      const response = await fetchWithAuth(apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ articles }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.success) {
        // Update the data with filtered results while preserving original read status
        const filteredArticles = result.filtered_articles.map((article, index) => {
          // Find the original article to preserve ALL original data
          // Try multiple matching strategies to find the original article
          const originalArticle = data.find(orig => {
            // Match by ID first (most reliable)
            if (orig.id === article.article_id || orig.id === article.id) {
              return true;
            }
            // Match by title (fallback)
            if (orig.title && article.title && orig.title.trim() === article.title.trim()) {
              return true;
            }
            // Match by index if available
            if (article.article_id && typeof article.article_id === 'number' && orig.id === article.article_id) {
              return true;
            }
            return false;
          });
          
          
          // If we found the original article, preserve ALL its data and add AI predictions
          if (originalArticle) {
            return {
              ...originalArticle, // Preserve ALL original article data (journal, link, year, etc.)
              id: originalArticle.id, // Keep original ID
              ai_prediction: article.prediction,
              ai_confidence: article.confidence,
              ai_explanation: article.explanation,
              ai_reasoning: article.reasoning,
            };
          } else {
            // Fallback if original article not found
          return {
            ...article,
              id: article.article_id || article.id || `filtered_${index}`,
            ai_prediction: article.prediction,
            ai_confidence: article.confidence,
            ai_explanation: article.explanation,
            ai_reasoning: article.reasoning,
              read: false,
              read_date: null
          };
          }
        });
        
        // Sort articles by AI prediction: yes -> maybe -> no, then by title alphabetically
        const predictionOrder = { 'yes': 0, 'maybe': 1, 'no': 2 };
        const sortedFilteredArticles = filteredArticles.sort((a, b) => {
          // Primary sort: by prediction (yes -> maybe -> no)
          const orderA = predictionOrder[a.ai_prediction] ?? 3; // Unknown predictions go last
          const orderB = predictionOrder[b.ai_prediction] ?? 3;
          const predDiff = orderA - orderB;
          if (predDiff !== 0) return predDiff;
          
          // Secondary sort: by title alphabetically
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });
        
        setAiFilteredData(sortedFilteredArticles);
        setOriginalAiFilteredData(sortedFilteredArticles);
        setAiFiltering(true);
        
        // Save AI predictions to database
        try {
          const saveResponse = await fetchWithAuth('/api/save-ai-predictions', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              articles: sortedFilteredArticles.map(article => ({
                query_index: article.query_index || 0,
                query_id: article.unique_query_id,  // Pass unique ID for safety
                article_index: article.article_index || article.id,
                ai_prediction: article.ai_prediction,
                ai_confidence: article.ai_confidence,
                ai_explanation: article.ai_explanation,
                ai_reasoning: article.ai_reasoning,
                domain: domain
              }))
            }),
          });
          
          if (saveResponse.ok) {
            console.log('✅ AI predictions saved to database');
          } else {
            console.error('⚠️ Failed to save AI predictions to database');
          }
        } catch (saveError) {
          console.error('Error saving AI predictions:', saveError);
          // Don't fail the whole operation if saving fails
        }
        // Summary will be computed via useMemo and sent via useEffect
      } else {
        throw new Error(result.error || 'Unknown error occurred');
      }
    } catch (error) {
      console.error('Error filtering articles:', error);
      showNotification('Error filtering articles: ' + error.message, 'error');
    } finally {
      setAiLoading(false);
    }
  }, [aiLoading, domain, data]);

  // Load saved AI classifications or trigger AI filtering when domain is provided
  useEffect(() => {
    const hasTriggeredForThisCombination = hasTriggeredFiltering.domain === domain && hasTriggeredFiltering.dataLength === data.length;
    
    // Check if articles already have AI predictions saved
    const hasSavedPredictions = data.some(article => article.ai_prediction !== undefined);
    
    if (domain && domain !== '' && data.length > 0 && !aiFiltering && !loading && !hasTriggeredForThisCombination && data.some(article => article.title)) {
      if (hasSavedPredictions) {
        // Load saved classifications from database
        console.log('📂 Loading saved AI classifications from database');
        const articlesWithPredictions = data.filter(article => article.ai_prediction);
        
        if (articlesWithPredictions.length > 0) {
          // Sort by prediction (yes/maybe/no), then by title alphabetically
          const predictionOrder = { 'yes': 0, 'maybe': 1, 'no': 2 };
          const sortedArticles = [...data].sort((a, b) => {
            // Articles without predictions go last
            if (!a.ai_prediction) return 1;
            if (!b.ai_prediction) return -1;
            
            // Primary sort: by prediction (yes -> maybe -> no)
            const orderA = predictionOrder[a.ai_prediction] ?? 3;
            const orderB = predictionOrder[b.ai_prediction] ?? 3;
            const predDiff = orderA - orderB;
            if (predDiff !== 0) return predDiff;
            
            // Secondary sort: by title alphabetically
            const titleA = (a.title || '').toLowerCase();
            const titleB = (b.title || '').toLowerCase();
            return titleA.localeCompare(titleB);
          });
          
          setAiFilteredData(sortedArticles);
          setOriginalAiFilteredData(sortedArticles);
          setAiFiltering(true);
          setHasTriggeredFiltering({ domain, dataLength: data.length });
          
          // Update summary
          if (onAIFilteringSummary) {
            const summary = {
              selected: sortedArticles.filter(article => article.ai_prediction === 'yes').length,
              maybe: sortedArticles.filter(article => article.ai_prediction === 'maybe').length,
              notSelected: sortedArticles.filter(article => article.ai_prediction === 'no').length,
              total: sortedArticles.length
            };
            onAIFilteringSummary(summary);
          }
        }
      } else {
        // No saved predictions, run AI filtering and save to database
        console.log('🚀 Running AI filtering for the first time and saving to database');
        setHasTriggeredFiltering({ domain, dataLength: data.length });
        
      setTimeout(() => {
      handleAIFiltering().catch(error => {
        console.error('Automatic filtering failed:', error);
          });
        }, 100);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, data.length, aiFiltering, loading, data]); // Removed onAIFilteringSummary - using ref now

  // Compute visible rows using useMemo - no more effects or duplicate arrays
  // Use aiFilteredData (not originalAiFilteredData) so manual edits are immediately visible
  const baseRows = aiFiltering ? aiFilteredData : data;
  const visibleRows = useMemo(
    () => hiddenNoArticles && aiFiltering 
      ? baseRows.filter(r => r.ai_prediction !== 'no') 
      : baseRows,
  // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseRows, hiddenNoArticles, aiFiltering]
  );

  // Handle cell edit commits (free grid compatible) - defined after baseRows
  const handleCellEditCommit = useCallback((params, event) => {
    if (params.field !== 'ai_prediction') return;

    const newPrediction = String(params.value);
    setAiFilteredData(prev => {
      const order = { yes: 0, maybe: 1, no: 2 };
      const updated = prev.map(r =>
        r.id === params.id
          ? {
              ...r,
              ai_prediction: newPrediction,
              user_classification: newPrediction,
              original_ai_prediction: r.original_ai_prediction ?? r.ai_prediction,
              original_ai_confidence: r.original_ai_confidence ?? r.ai_confidence,
              classification_modified: true,
              classification_modified_date: new Date().toISOString(),
            }
          : r
      );
      
      // Sort by prediction (yes/maybe/no), then by title alphabetically
      const oldBucket = order[prev.find(r => r.id === params.id)?.ai_prediction] ?? 3;
      const newBucket = order[newPrediction] ?? 3;
      
      if (oldBucket !== newBucket) {
        updated.sort((a, b) => {
          // Primary sort: by prediction (yes -> maybe -> no)
          const predDiff = (order[a.ai_prediction] ?? 3) - (order[b.ai_prediction] ?? 3);
          if (predDiff !== 0) return predDiff;
          
          // Secondary sort: by title alphabetically
          const titleA = (a.title || '').toLowerCase();
          const titleB = (b.title || '').toLowerCase();
          return titleA.localeCompare(titleB);
        });
      }
      
      // Notify parent of the update
      if (onArticleUpdate) {
        const updatedArticle = updated.find(r => r.id === params.id);
        if (updatedArticle) {
          onArticleUpdate(updatedArticle);
        }
      }
      
      return updated;
    });

    // Fire-and-forget DB save
    const row = baseRows.find(r => r.id === params.id);
    const oldPred = row?.original_ai_prediction ?? row?.ai_prediction;
    const oldConf = row?.original_ai_confidence ?? row?.ai_confidence;

    fetchWithAuth('/api/update-classification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query_id: row?.query_index || 0,
        article_id: row?.article_index || params.id,
        user_classification: newPrediction,
        original_prediction: oldPred,
        original_confidence: oldConf,
        domain,
      }),
    }).catch(console.error);
  }, [baseRows, domain, setAiFilteredData, onArticleUpdate]);
  
  // Compute summary using useMemo - derived from base rows (not visible)
  const summary = useMemo(() => {
    if (!aiFiltering || baseRows.length === 0) return null;
    
    let yes = 0, maybe = 0, no = 0;
    for (const r of baseRows) {
      if (r.ai_prediction === 'yes') yes++;
      else if (r.ai_prediction === 'maybe') maybe++;
      else if (r.ai_prediction === 'no') no++;
    }
      return {
      selected: yes, 
      maybe, 
      notSelected: no, 
      total: baseRows.length 
    };
  }, [baseRows, aiFiltering]);
  
  // Send summary to parent when it changes - use ref to prevent infinite loops
  const prevSummaryRef = React.useRef(null);
  useEffect(() => {
    if (summary && onAIFilteringSummaryRef.current) {
      // Only call if summary actually changed (prevent infinite loops)
      const summaryChanged = !prevSummaryRef.current || 
        prevSummaryRef.current.selected !== summary.selected ||
        prevSummaryRef.current.maybe !== summary.maybe ||
        prevSummaryRef.current.notSelected !== summary.notSelected ||
        prevSummaryRef.current.total !== summary.total;
      
      if (summaryChanged) {
        prevSummaryRef.current = summary;
        onAIFilteringSummaryRef.current(summary);
      }
    }
  }, [summary]); // Only depend on summary, not the callback
  
  // Sync pagination with parent currentPage prop
  useEffect(() => {
    setPaginationModel(m => ({ ...m, page: currentPage }));
  }, [currentPage]);

  // Static column definitions with minimal deps - moved outside useMemo for maximum stability
  // Use flex sizing for responsive columns that always fit container width
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => {
    const baseColumns = [
      {
        field: 'truncated_title',
        headerName: 'Title',
        flex: 2,
        minWidth: 220,
        // No valueFormatter needed - precomputed field
      },
      {
        field: 'truncated_abstract',
        headerName: 'Abstract',
        flex: 3,
        minWidth: 280,
        // No valueFormatter needed - precomputed field
      },
      {
        field: 'link',
        headerName: 'Link',
        flex: 1.2,
        minWidth: 160,
        renderCell: (params) => <LinkCell value={params.value} />,
      },
      {
        field: 'year',
        headerName: 'Year',
        flex: 0.6,
        minWidth: 80,
        // No renderCell - default is fastest
      },
      {
        field: 'journal',
        headerName: 'Journal',
        flex: 1.4,
        minWidth: 160,
        // No renderCell - default is fastest
      }
    ];

  // Add AI prediction column if filtering is active
  if (aiFiltering && aiFilteredData.length > 0) {
      baseColumns.push({
      field: 'ai_prediction',
      headerName: `${domain} AI Prediction`,
      flex: 1,
      minWidth: 150,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <PredictionCell 
          id={params.id}
          prediction={params.row.ai_prediction}
          confidence={params.row.ai_confidence}
          modified={params.row.classification_modified}
          origPred={params.row.original_ai_prediction}
          origConf={params.row.original_ai_confidence}
          onEditCommit={handleCellEditCommit}
        />
      )
    });
  }

    // Add category column
    baseColumns.push({
      field: 'category',
      headerName: 'Category',
      flex: 1,
      minWidth: 150, // Match AI Prediction column width
      sortable: true,
      filterable: true,
      renderCell: (params) => (
        <CategoryCell 
          row={params.row}
          categories={categories}
          onCategoryChange={onCategoryChange}
          isDarkMode={isDarkMode}
        />
      )
    });

    // Add actions column
    baseColumns.push({
      field: 'actions',
      headerName: 'Actions',
      width: 160, // Fixed width so buttons don't jump
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <ActionsCell
          id={params.id}
          read={params.row.read}
          onToggleRead={handleMarkAsReadRef.current}
          onShare={handleShareArticleRef.current}
        />
      ),
    });

  // Filter out null columns (like the removed 'read' column)
    const filteredColumns = baseColumns.filter(column => column !== null);
    
    return filteredColumns;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiFiltering, domain, apiRef]); // Minimal deps - handlers via refs

  // Early returns after all hooks
  if (loading) {
    return <p>Loading...</p>;
  }
  if (!data.length) {
    return <p>No data available</p>;
  }
  
  return (
    <>
    <Box sx={{ width: '100%', minWidth: 0, maxWidth: '100%', overflow: 'hidden' }}>
    <DataGrid
      apiRef={apiRef}
      // Removed key to prevent forced remounts - let DataGrid manage its own state
      rows={visibleRows}
      getRowId={(row) => row.id ?? row.doi ?? row.link ?? `${row.title}-${row.year}`}
      columns={columns}
      paginationModel={paginationModel}
      onPaginationModelChange={(model) => {
        setPaginationModel(model);
        if (onPageChange && model.page !== currentPage) {
          onPageChange(model.page);
        }
      }}
      pageSizeOptions={[5, 10, 20, 50]}
      pagination
      paginationMode="client"
      disableSelectionOnClick
      autoHeight
      rowHeight={120} // Fixed row height
      columnHeaderHeight={48} // Fixed header height
      rowBuffer={1} // Minimal buffer for autoHeight
      columnBuffer={1} // Minimal buffer for autoHeight
      getRowClassName={(params) => {
        return params.row.read ? 'read-row' : 'unread-row';
      }}
      onRowClick={handleRowClick}
      disableRowClickOnSelection
      // Performance optimizations
      disableColumnReorder
      disableColumnMenu
      hideFooterSelectedRowCount
      sx={{
        ...dataGridStyles,
        width: '100%', // Ensure full container width
        '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': { 
          boxSizing: 'border-box'
        },
        '& .MuiDataGrid-virtualScroller': { 
          overflowX: 'hidden' // Prevent accidental horizontal scroll
        }
      }}
    />
    </Box>
    
    {/* Share Article Dialog */}
    <Dialog
      open={shareDialogOpen}
      onClose={() => setShareDialogOpen(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          background: 'var(--surface-2)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-lg)',
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--text-primary)', borderBottom: '1px solid var(--glass-border)' }}>
        Share Article
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        {selectedArticle && (
          <Box sx={{ mb: 3, p: 2, bgcolor: 'var(--bg-secondary)', borderRadius: 'var(--radius-md)', border: '1px solid var(--glass-border)' }}>
            <Typography variant="subtitle2" sx={{ color: 'var(--success)', mb: 1 }}>
              Article to Share
            </Typography>
            <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
              {selectedArticle.title || 'No title'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'var(--text-secondary)', display: 'block', mt: 1 }}>
              {selectedArticle.authors} • {selectedArticle.journal} • {selectedArticle.year}
            </Typography>
          </Box>
        )}

        <TextField
          fullWidth
          label="Search Users"
          value={userSearchQuery}
          onChange={(e) => setUserSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            sx: { color: 'var(--text-primary)' }
          }}
          InputLabelProps={{
            sx: { color: 'var(--text-secondary)' }
          }}
        />

        <Box sx={{ maxHeight: '200px', overflow: 'auto', mb: 2 }}>
          {(() => {
            // If there's a search query, show all matching users
            if (userSearchQuery.trim()) {
              return users
                .filter(user => 
                  user.name.toLowerCase().includes(userSearchQuery.toLowerCase()) ||
                  user.email.toLowerCase().includes(userSearchQuery.toLowerCase())
                )
                .map((user) => (
                  <ListItem
                    key={user.id}
                    button
                    onClick={() => setSelectedUser(user)}
                    sx={{
                      bgcolor: selectedUser?.id === user.id ? 'rgba(108, 48, 130, 0.1)' : 'transparent',
                      border: selectedUser?.id === user.id ? '1px solid var(--neon-cyan)' : '1px solid transparent',
                      borderRadius: 'var(--radius-md)',
                      mb: 1,
                      '&:hover': {
                        bgcolor: 'rgba(255, 255, 255, 0.05)'
                      }
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                          {user.name}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                          {user.email} • {user.role}
                        </Typography>
                      }
                    />
                  </ListItem>
                ));
            }
            
            // If no search query, show only the last shared user (if exists)
            if (lastSharedUser) {
              return (
                <ListItem
                  key={lastSharedUser.id}
                  button
                  onClick={() => setSelectedUser(lastSharedUser)}
                  sx={{
                    bgcolor: selectedUser?.id === lastSharedUser.id ? 'rgba(108, 48, 130, 0.1)' : 'transparent',
                    border: selectedUser?.id === lastSharedUser.id ? '1px solid var(--neon-cyan)' : '1px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.05)'
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" sx={{ color: 'var(--text-primary)', fontWeight: 'bold' }}>
                        {lastSharedUser.name}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: 'var(--text-secondary)' }}>
                        {lastSharedUser.email} • {lastSharedUser.role} • Last shared with
                      </Typography>
                    }
                  />
                </ListItem>
              );
            }
            
            // If no last shared user and no search, show message
            return (
              <Box sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="body2" sx={{ color: 'var(--text-muted)' }}>
                  Start typing to search for users
                </Typography>
              </Box>
            );
          })()}
        </Box>

        <TextField
          fullWidth
          label="Message (optional)"
          value={shareMessage}
          onChange={(e) => setShareMessage(e.target.value)}
          placeholder="Add a message to accompany the shared article..."
          multiline
          rows={2}
          InputProps={{
            sx: { color: 'var(--text-primary)' }
          }}
          InputLabelProps={{
            sx: { color: 'var(--text-secondary)' }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ p: 3, borderTop: '1px solid var(--glass-border)' }}>
        <Button
          onClick={() => {
            setShareDialogOpen(false);
            setSelectedUser(null);
            setShareMessage('');
            setUserSearchQuery('');
            setSelectedArticle(null);
          }}
          className="btn-modern btn-modern-secondary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleShareToUser}
          disabled={!selectedUser || sending}
          className="btn-modern btn-modern-primary"
        >
          {sending ? 'Sharing...' : 'Share Article'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Styled Snackbar for Notifications */}
    <Snackbar
      open={snackbar.open}
      autoHideDuration={4000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert 
        onClose={handleCloseSnackbar} 
        severity={snackbar.severity}
        variant="filled"
        sx={{
          width: '100%',
          backgroundColor: snackbar.severity === 'warning' 
            ? 'rgba(255, 152, 0, 0.9)' 
            : snackbar.severity === 'error'
            ? 'rgba(244, 67, 54, 0.9)'
            : snackbar.severity === 'success'
            ? 'rgba(70, 130, 48, 0.9)'
            : 'rgba(108, 48, 130, 0.9)',
          color: '#ffffff',
          border: snackbar.severity === 'warning'
            ? '1px solid rgba(255, 152, 0, 0.5)'
            : snackbar.severity === 'error'
            ? '1px solid rgba(244, 67, 54, 0.5)'
            : snackbar.severity === 'success'
            ? '1px solid rgba(70, 130, 48, 0.5)'
            : '1px solid rgba(108, 48, 130, 0.5)',
          borderRadius: 'var(--radius-md)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          '& .MuiAlert-icon': {
            color: '#ffffff'
          }
        }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
});

export default DynamicTable;
